import type { HTMLAttributes } from "react";
import { formatPaise } from "../lib/cn";
import { cn } from "../lib/cn";

export interface PriceTagProps extends HTMLAttributes<HTMLDivElement> {
  pricePaise: number;
  unit?: string | null;
  mrpPaise?: number | null;
  size?: "sm" | "md" | "lg";
  align?: "left" | "right" | "between";
  showSavings?: boolean;
}

export function PriceTag({
  pricePaise,
  unit,
  mrpPaise,
  size = "md",
  align = "between",
  showSavings = true,
  className,
  ...props
}: PriceTagProps) {
  const hasMrp = mrpPaise != null && mrpPaise > pricePaise;
  const savingsPaise = hasMrp ? mrpPaise - pricePaise : 0;
  const savingsPercent = hasMrp ? Math.round((savingsPaise / mrpPaise) * 100) : 0;

  const priceTextSize = {
    sm: "text-sm",
    md: "text-base sm:text-lg",
    lg: "text-xl sm:text-2xl",
  }[size];

  const layoutClass = {
    left: "flex flex-col items-start gap-0.5",
    right: "flex flex-col items-end gap-0.5 text-right",
    between: "flex items-baseline justify-between gap-1 w-full",
  }[align];

  return (
    <div className={cn(layoutClass, className)} {...props}>
      <div className="flex flex-col leading-tight">
        <span className={cn("font-extrabold text-foreground tracking-tight type-num", priceTextSize)}>
          {formatPaise(pricePaise)}
        </span>
        {unit && (
          <span className="text-[10px] text-muted-foreground font-medium">
            per {unit}
          </span>
        )}
      </div>

      {hasMrp && (
        <div className={cn("flex flex-col", align === "between" ? "text-right" : "")}>
          <span className="text-[11px] font-medium text-muted-foreground line-through type-num">
            MRP {formatPaise(mrpPaise)}
          </span>
          {showSavings && savingsPaise > 0 && (
            <span className="text-[10px] font-bold text-tone-success-text type-num">
              Save {formatPaise(savingsPaise)} ({savingsPercent}%)
            </span>
          )}
        </div>
      )}
    </div>
  );
}
